import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Table,
  Badge,
  Modal,
} from "react-bootstrap";
import Select from "react-select";
import { Edit2, Trash2, Plus, Calendar, Download } from "lucide-react";
import Layout from "../../layout/Layout";
import { useAuth } from "../../context/AuthContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  cleaningPlanInsert,
  cleaningPlanUpdate,
  cleaningPlanDelete,
  cleaningPlanList,
  getPersonels,
} from "../../../api/bakim/machineCleaning";

const monthsList = [
  { id: 1, name: "Oca", fullName: "Ocak" },
  { id: 2, name: "Sub", fullName: "Subat" },
  { id: 3, name: "Mar", fullName: "Mart" },
  { id: 4, name: "Nis", fullName: "Nisan" },
  { id: 5, name: "May", fullName: "Mayis" },
  { id: 6, name: "Haz", fullName: "Haziran" },
  { id: 7, name: "Tem", fullName: "Temmuz" },
  { id: 8, name: "Agu", fullName: "Agustos" },
  { id: 9, name: "Eyl", fullName: "Eylul" },
  { id: 10, name: "Eki", fullName: "Ekim" },
  { id: 11, name: "Kas", fullName: "Kasim" },
  { id: 12, name: "Ara", fullName: "Aralik" },
];

// PDF'te bozuk cikan Turkce karakterleri Ingilizceye ceviren fonksiyon
const trToEn = (text) => {
  if (!text) return "";
  return text
    .replace(/Ğ/g, "G")
    .replace(/ğ/g, "g")
    .replace(/Ü/g, "U")
    .replace(/ü/g, "u")
    .replace(/Ş/g, "S")
    .replace(/ş/g, "s")
    .replace(/İ/g, "I")
    .replace(/ı/g, "i")
    .replace(/Ö/g, "O")
    .replace(/ö/g, "o")
    .replace(/Ç/g, "C")
    .replace(/ç/g, "c");
};

function CleaningPlanManagement() {
  const { user } = useAuth();
  const permissions = user?.permissions || [];

  const canInsert = permissions.includes("cleaningPlan_insert");
  const canUpdate = permissions.includes("cleaningPlan_update");
  const canDelete = permissions.includes("cleaningPlan_delete");

  const [plans, setPlans] = useState([]);
  const [personels, setPersonels] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  const initialFormState = {
    machineName: "",
    operation: "",
    year: new Date().getFullYear(),
    repeatPerYear: "",
    responsibleUsers: [],
    months: [],
  };
  const [formData, setFormData] = useState(initialFormState);

  const initialStatusForm = {
    planId: null,
    monthId: null,
    status: "planned",
    doneDate: "",
  };
  const [statusForm, setStatusForm] = useState(initialStatusForm);

  useEffect(() => {
    fetchPersonels();
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await cleaningPlanList({ year: selectedYear });
      setPlans(res.data.data);
    } catch (err) {
      console.error("Plan yukleme hatasi:", err);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const fetchPersonels = async () => {
    try {
      const res = await getPersonels();
      setPersonels(res.data.data.map((p) => ({ value: p._id, label: p.name })));
    } catch (err) {
      console.error("Personel yukleme hatasi:", err);
    }
  };

  const handleOpenPlanModal = (plan = null) => {
    if (plan && !canUpdate) return;
    if (!plan && !canInsert) return;

    if (plan) {
      setEditingPlan(plan);
      setFormData({
        machineName: plan.machineName,
        operation: plan.operation,
        year: plan.year,
        repeatPerYear: plan.repeatPerYear || "",
        responsibleUsers: plan.responsibleUsers.map((u) =>
          typeof u === "object"
            ? { value: u._id, label: u.name }
            : { value: u, label: u }
        ),
        months: plan.months,
      });
    } else {
      setEditingPlan(null);
      setFormData({ ...initialFormState, year: selectedYear });
    }
    setShowPlanModal(true);
  };

  const handleSavePlan = async () => {
    try {
      const payload = {
        ...formData,
        repeatPerYear: formData.repeatPerYear
          ? parseInt(formData.repeatPerYear, 10)
          : undefined,
        responsibleUsers: formData.responsibleUsers.map((u) => u.value),
      };

      if (editingPlan) {
        if (!canUpdate) return;
        await cleaningPlanUpdate({ _id: editingPlan._id, ...payload });
      } else {
        if (!canInsert) return;
        await cleaningPlanInsert(payload);
      }

      setShowPlanModal(false);
      fetchPlans();
    } catch (err) {
      alert("Islem sirasinda bir hata olustu");
    }
  };

  const handleDelete = async (id) => {
    if (!canDelete) return;
    if (window.confirm("Bu plani silmek istediginize emin misiniz?")) {
      try {
        await cleaningPlanDelete({ deletedId: id });
        fetchPlans();
      } catch (err) {
        alert("Silme islemi basarisiz");
      }
    }
  };

  const getMonthData = (plan, monthId) => {
    return plan.months.find((m) => m.month === monthId);
  };

  const handleMonthCellClick = async (plan, monthId) => {
    if (!canUpdate) return;

    const existingMonth = getMonthData(plan, monthId);

    if (!existingMonth) {
      const updatedMonths = [
        ...plan.months,
        { month: monthId, status: "planned" },
      ];
      try {
        await cleaningPlanUpdate({ _id: plan._id, months: updatedMonths });
        fetchPlans();
      } catch (err) {
        console.error("Hizli ekleme hatasi");
      }
    } else {
      setStatusForm({
        planId: plan._id,
        monthId: monthId,
        status: existingMonth.status || "planned",
        doneDate: existingMonth.doneDate
          ? existingMonth.doneDate.split("T")[0]
          : "",
      });
      setShowStatusModal(true);
    }
  };

  const handleSaveStatus = async () => {
    if (!canUpdate) return;
    try {
      const plan = plans.find((p) => p._id === statusForm.planId);
      if (!plan) return;

      const updatedMonths = plan.months.map((m) => {
        if (m.month === statusForm.monthId) {
          return {
            ...m,
            status: statusForm.status,
            doneDate: statusForm.status === "done" ? statusForm.doneDate : null,
          };
        }
        return m;
      });

      await cleaningPlanUpdate({ _id: plan._id, months: updatedMonths });
      setShowStatusModal(false);
      fetchPlans();
    } catch (err) {
      alert("Durum guncelleme basarisiz oldu.");
    }
  };

  const handleRemoveMonth = async () => {
    if (!canUpdate) return;
    try {
      const plan = plans.find((p) => p._id === statusForm.planId);
      const updatedMonths = plan.months.filter(
        (m) => m.month !== statusForm.monthId
      );
      await cleaningPlanUpdate({ _id: plan._id, months: updatedMonths });
      setShowStatusModal(false);
      fetchPlans();
    } catch (err) {
      alert("Ay kaldirilamadi.");
    }
  };

  // --- PDF EXPORT FONKSIYONU ---
  const handleExportPDF = () => {
    const doc = new jsPDF("landscape", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const tableColumn = [
      "MAKINA / ISLEM",
      "TEKRAR",
      "SORUMLU",
      ...monthsList.map((m) => m.name),
    ];

    const tableRows = [];
    plans.forEach((plan) => {
      // Turkce karakter donusumu ve bosluk temizligi
      const cleanOperation = plan.operation
        ? trToEn(plan.operation.replace(/\s+/g, " ").trim())
        : "";

      const cleanMachineName = trToEn(plan.machineName);

      const machineInfo = cleanOperation
        ? `${cleanMachineName}\n(${cleanOperation})`
        : cleanMachineName;

      const repeat = plan.repeatPerYear ? `${plan.repeatPerYear}/YIL` : "-";

      // Personel isimlerindeki Turkce karakterleri de duzeltiyoruz
      const responsible = plan.responsibleUsers
        ?.map((u) => trToEn(u.name) || "Personel")
        .join(", ");

      const rowData = [machineInfo, repeat, responsible];

      monthsList.forEach((m) => {
        const mData = getMonthData(plan, m.id);
        let cellText = "";
        if (mData) {
          if (mData.status === "planned") cellText = "P";
          else if (mData.status === "not_done") cellText = "X";
          else if (mData.status === "done") {
            cellText = "G";
            if (mData.doneDate) {
              const d = new Date(mData.doneDate);
              const formattedDate = `${String(d.getDate()).padStart(
                2,
                "0"
              )}.${String(d.getMonth() + 1).padStart(2, "0")}`;
              cellText += `\n${formattedDate}`;
            }
          }
        }
        rowData.push(cellText);
      });

      tableRows.push(rowData);
    });

    // Tek sayfaya sigdirma optimizasyonu
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 80,
      theme: "grid",
      styles: {
        fontSize: 7,
        cellPadding: 2,
        halign: "center",
        valign: "middle",
        lineColor: [0, 0, 0],
        lineWidth: 0.5,
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [220, 220, 220],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 7,
      },
      columnStyles: {
        0: { halign: "left", cellWidth: 160 },
        1: { cellWidth: 40 },
        2: { cellWidth: 80 },
      },
      margin: { bottom: 60 },
      didDrawPage: function (data) {
        // Kurumsal Header (Solid Forge tarzi)
        const startX = data.settings.margin.left;
        const startY = 25;
        const headerHeight = 45;
        const tableWidth = pageWidth - startX * 2;

        doc.setDrawColor(0);
        doc.setLineWidth(1);
        doc.rect(startX, startY, tableWidth, headerHeight);

        doc.line(startX + 120, startY, startX + 120, startY + headerHeight);
        doc.line(
          pageWidth - startX - 160,
          startY,
          pageWidth - startX - 160,
          startY + headerHeight
        );

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("FIRMA LOGO", startX + 60, startY + 26, { align: "center" });

        doc.setFontSize(13);
        doc.text(
          `MAKINA TEMIZLIK PLANI - ${selectedYear}`,
          pageWidth / 2,
          startY + 26,
          { align: "center" }
        );

        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        const rightColX = pageWidth - startX - 155;
        const rightValX = pageWidth - startX - 70;

        const rowH = headerHeight / 4;
        doc.line(
          pageWidth - startX - 160,
          startY + rowH,
          pageWidth - startX,
          startY + rowH
        );
        doc.line(
          pageWidth - startX - 160,
          startY + rowH * 2,
          pageWidth - startX,
          startY + rowH * 2
        );
        doc.line(
          pageWidth - startX - 160,
          startY + rowH * 3,
          pageWidth - startX,
          startY + rowH * 3
        );

        const textOffsetY = 8;
        doc.text("Dokuman No", rightColX, startY + textOffsetY);
        doc.text(": FR-001", rightValX, startY + textOffsetY);

        doc.text("Yayin Tarihi", rightColX, startY + rowH + textOffsetY);
        doc.text(
          `: ${new Date().toLocaleDateString("tr-TR")}`,
          rightValX,
          startY + rowH + textOffsetY
        );

        doc.text("Revizyon No", rightColX, startY + rowH * 2 + textOffsetY);
        doc.text(": 00", rightValX, startY + rowH * 2 + textOffsetY);

        doc.text("Sayfa No", rightColX, startY + rowH * 3 + textOffsetY);
        doc.text(
          `: ${data.pageNumber}`,
          rightValX,
          startY + rowH * 3 + textOffsetY
        );
      },
    });

    let finalY = doc.lastAutoTable.finalY || 80;

    if (finalY + 60 > pageHeight) {
      doc.addPage();
      finalY = 80;
    }

    const signatureY = finalY + 35;
    const colWidth = pageWidth / 3;

    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);

    doc.setFont("helvetica", "bold");
    doc.text("HAZIRLAYAN", colWidth / 2, signatureY, { align: "center" });
    doc.text("KONTROL EDEN", colWidth + colWidth / 2, signatureY, {
      align: "center",
    });
    doc.text("ONAYLAYAN", colWidth * 2 + colWidth / 2, signatureY, {
      align: "center",
    });

    doc.setFont("helvetica", "normal");
    doc.text("KALITE MUHENDISI", colWidth / 2, signatureY + 15, {
      align: "center",
    });
    doc.text("URETIM MUDURU", colWidth + colWidth / 2, signatureY + 15, {
      align: "center",
    });
    doc.text("GN. MDR. YRD.", colWidth * 2 + colWidth / 2, signatureY + 15, {
      align: "center",
    });

    doc.save(`Temizlik_Planlari_${selectedYear}.pdf`);
  };

  const renderMonthBox = (plan, monthId) => {
    const mData = getMonthData(plan, monthId);
    let boxClass = "month-box-empty";
    let text = "";
    let dateText = "";

    if (mData) {
      if (mData.status === "planned") {
        boxClass = "month-box-planned";
        text = "P";
      } else if (mData.status === "done") {
        boxClass = "month-box-done";
        text = "G";
        if (mData.doneDate) {
          const d = new Date(mData.doneDate);
          dateText = `${String(d.getDate()).padStart(2, "0")}.${String(
            d.getMonth() + 1
          ).padStart(2, "0")}`;
        }
      } else if (mData.status === "not_done") {
        boxClass = "month-box-not-done";
        text = "X";
      }
    }

    return (
      <div
        onClick={() => handleMonthCellClick(plan, monthId)}
        className={`table-month-indicator flex-column ${boxClass} ${
          !canUpdate ? "cursor-not-allowed" : ""
        }`}
        title={canUpdate ? "Detay icin tiklayin" : "Yetkiniz yok"}
      >
        <span style={{ lineHeight: dateText ? "1" : "inherit" }}>{text}</span>
        {dateText && (
          <span
            style={{
              fontSize: "9px",
              marginTop: "2px",
              fontWeight: "normal",
              opacity: "0.9",
            }}
          >
            {dateText}
          </span>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div
        className="p-4"
        style={{ backgroundColor: "#f4f7f6", minHeight: "100vh" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 className="fw-bold text-secondary mb-0">
              Makina Temizlik Planlari
            </h3>
            <p className="text-muted small mb-0">
              Yillik periyodik bakim ve temizlik takibi
            </p>
          </div>
          <div className="d-flex gap-3 align-items-center">
            <div className="d-flex align-items-center gap-2 bg-white px-3 py-1 border rounded shadow-sm">
              <Calendar size={16} className="text-muted" />
              <Form.Select
                size="sm"
                className="border-0 fw-bold text-dark shadow-none"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{ cursor: "pointer", minWidth: "90px" }}
              >
                {[...Array(5)].map((_, i) => {
                  const yr = new Date().getFullYear() - 2 + i;
                  return (
                    <option key={yr} value={yr}>
                      {yr}
                    </option>
                  );
                })}
              </Form.Select>
            </div>

            <Button
              variant="outline-secondary"
              className="d-flex align-items-center gap-2 shadow-sm bg-white"
              onClick={handleExportPDF}
            >
              <Download size={18} /> PDF Indir
            </Button>

            {canInsert && (
              <Button
                variant="primary"
                className="d-flex align-items-center gap-2 shadow-sm"
                onClick={() => handleOpenPlanModal()}
              >
                <Plus size={18} /> Yeni Plan
              </Button>
            )}
          </div>
        </div>

        <div className="d-flex gap-4 mb-3 ps-2 small fw-semibold text-secondary">
          <div className="d-flex align-items-center gap-2">
            <span className="legend-box month-box-planned"></span> Planlandi (P)
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="legend-box month-box-done"></span> Gerceklesti (G)
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="legend-box month-box-not-done"></span>{" "}
            Gerceklesmedi (X)
          </div>
        </div>

        <Card className="border-0 shadow-sm border-radius-15">
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0 custom-table align-middle">
              <thead>
                <tr>
                  <th className="ps-4" style={{ width: "30%" }}>
                    MAKINA / ISLEM
                  </th>
                  <th className="text-center" style={{ width: "5%" }}>
                    TEKRAR
                  </th>
                  <th style={{ width: "15%" }}>SORUMLU</th>
                  {monthsList.map((m) => (
                    <th
                      key={m.id}
                      className="text-center"
                      style={{ width: "3%" }}
                    >
                      {m.name}
                    </th>
                  ))}
                  {(canUpdate || canDelete) && (
                    <th className="text-end pe-4" style={{ width: "10%" }}>
                      YONET
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {plans.length === 0 ? (
                  <tr>
                    <td
                      colSpan={canUpdate || canDelete ? "16" : "15"}
                      className="text-center py-4 text-muted"
                    >
                      Secili yila ait plan bulunamadi.
                    </td>
                  </tr>
                ) : (
                  plans.map((plan) => (
                    <tr key={plan._id}>
                      <td className="ps-4 py-3">
                        <div className="fw-bold text-dark">
                          {plan.machineName}
                        </div>
                        <div className="text-muted small">{plan.operation}</div>
                      </td>
                      <td className="text-center fw-medium text-secondary">
                        {plan.repeatPerYear ? `${plan.repeatPerYear}/YIL` : "-"}
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {plan.responsibleUsers?.slice(0, 2).map((u, i) => (
                            <Badge
                              key={i}
                              bg="light"
                              text="dark"
                              className="border fw-normal"
                            >
                              {u.name || "Personel"}
                            </Badge>
                          ))}
                          {plan.responsibleUsers?.length > 2 && (
                            <Badge bg="secondary">
                              +{plan.responsibleUsers.length - 2}
                            </Badge>
                          )}
                        </div>
                      </td>
                      {monthsList.map((m) => (
                        <td key={m.id} className="text-center p-1">
                          {renderMonthBox(plan, m.id)}
                        </td>
                      ))}
                      {(canUpdate || canDelete) && (
                        <td className="text-end pe-4">
                          <div className="d-flex justify-content-end gap-2">
                            {canUpdate && (
                              <Button
                                variant="outline-info"
                                size="sm"
                                className="btn-icon"
                                onClick={() => handleOpenPlanModal(plan)}
                              >
                                <Edit2 size={14} />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="btn-icon"
                                onClick={() => handleDelete(plan._id)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        <Modal
          show={showPlanModal}
          onHide={() => setShowPlanModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="fw-bold">
              {editingPlan ? "Plani Guncelle" : "Yeni Temizlik Plani"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">
                      Makina Adi
                    </Form.Label>
                    <Form.Control
                      value={formData.machineName}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          machineName: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">Yil</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.year}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          year: parseInt(e.target.value),
                        })
                      }
                      disabled={!!editingPlan}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">
                      Tekrar Sayisi (/Yil)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      placeholder="Orn: 3"
                      value={formData.repeatPerYear}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          repeatPerYear: e.target.value
                            ? parseInt(e.target.value)
                            : "",
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">
                      Sorumlu Personeller
                    </Form.Label>
                    <Select
                      isMulti
                      options={personels}
                      value={formData.responsibleUsers}
                      onChange={(selected) =>
                        setFormData({ ...formData, responsibleUsers: selected })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold small">
                      Islem Detayi
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.operation}
                      onChange={(e) =>
                        setFormData({ ...formData, operation: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Label className="fw-semibold small mb-2">
                    Planlanan Aylar (Varsayilan olarak 'Planlandi' atanir)
                  </Form.Label>
                  <div className="month-grid">
                    {monthsList.map((m) => {
                      const isActive = formData.months.some(
                        (x) => x.month === m.id
                      );
                      return (
                        <div
                          key={m.id}
                          className={`month-box ${isActive ? "selected" : ""}`}
                          onClick={() => {
                            let updated;
                            if (isActive) {
                              updated = formData.months.filter(
                                (x) => x.month !== m.id
                              );
                            } else {
                              updated = [
                                ...formData.months,
                                { month: m.id, status: "planned" },
                              ];
                            }
                            setFormData({ ...formData, months: updated });
                          }}
                        >
                          {m.fullName}
                        </div>
                      );
                    })}
                  </div>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer className="border-0 bg-light p-3">
            <Button
              variant="link"
              className="text-muted text-decoration-none"
              onClick={() => setShowPlanModal(false)}
            >
              Iptal
            </Button>
            <Button variant="primary" className="px-4" onClick={handleSavePlan}>
              Kaydet
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showStatusModal}
          onHide={() => setShowStatusModal(false)}
          centered
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="fw-bold fs-5">
              Islem Durumu Guncelle
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold small">Durum</Form.Label>
              <Form.Select
                value={statusForm.status}
                onChange={(e) =>
                  setStatusForm({ ...statusForm, status: e.target.value })
                }
              >
                <option value="planned">Planlandi (Bekliyor)</option>
                <option value="done">Gerceklesti (Tamamlandi)</option>
                <option value="not_done">
                  Gerceklesmedi (Iptal / Gecikti)
                </option>
              </Form.Select>
            </Form.Group>

            {statusForm.status === "done" && (
              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold small">
                  Gerceklesme Tarihi
                </Form.Label>
                <Form.Control
                  type="date"
                  value={statusForm.doneDate}
                  onChange={(e) =>
                    setStatusForm({ ...statusForm, doneDate: e.target.value })
                  }
                />
              </Form.Group>
            )}

            <div className="d-flex justify-content-between align-items-center">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleRemoveMonth}
              >
                Plandan Cikar
              </Button>
              <Button
                variant="success"
                className="px-4"
                onClick={handleSaveStatus}
              >
                Durumu Kaydet
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </div>

      <style>{`
        .custom-table thead th { background-color: #f8f9fa; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px; color: #6c757d; border-bottom: 1px solid #eee; vertical-align: middle; }
        .table-month-indicator { width: 100%; min-height: 40px; padding: 4px 0; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; cursor: pointer; transition: all 0.2s; border: 1px solid transparent; overflow: hidden; }
        
        .month-box-empty { background: #f8f9fa; border-color: #e9ecef; color: transparent; }
        .month-box-empty:hover { background: #e2e6ea; }
        
        .month-box-planned { background: #fff3cd; color: #856404; border-color: #ffeeba; }
        .month-box-planned:hover { background: #ffe8a1; }
        
        .month-box-done { background: #d1e7dd; color: #0f5132; border-color: #badbcc; }
        .month-box-done:hover { background: #bfe2d1; }
        
        .month-box-not-done { background: #f8d7da; color: #842029; border-color: #f5c2c7; }
        .month-box-not-done:hover { background: #f1b0b7; }

        .cursor-not-allowed { cursor: not-allowed !important; }

        .legend-box { display: inline-block; width: 14px; height: 14px; border-radius: 3px; border: 1px solid rgba(0,0,0,0.1); }
        .btn-icon { width: 32px; height: 32px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 6px; }
        .month-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; }
        .month-box { padding: 8px; border: 1px solid #dee2e6; border-radius: 6px; text-align: center; cursor: pointer; font-size: 13px; font-weight: 500; transition: 0.2s; background: #fff; color: #495057; }
        .month-box:hover { border-color: #0d6efd; background: #f8f9ff; }
        .month-box.selected { background: #0d6efd; color: white; border-color: #0d6efd; }
        .border-radius-15 { border-radius: 12px; overflow: hidden; }
      `}</style>
    </Layout>
  );
}

export default CleaningPlanManagement;
